import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import CheckoutForm from "./CheckoutForm";
import TipInput from "./TipInput";
import logo from '../assets/icons/logoWhite.png';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

export default function CheckoutWrapper() {
    const { '*': wildcard } = useParams();
    const linkId = wildcard.split('/')[0];
    const [stripeAccountId, setStripeAccountId] = useState(null);
    const [stripeProductId, setStripeProductId] = useState(null);
    const [moneyType, setMoneyType] = useState(null);
    const [stripeOnboardingStatus, setStripeOnboardingStatus] = useState(null);
    const [showCheckoutForm, setShowCheckoutForm] = useState(false);
    const [tipAmount, setTipAmount] = useState('');
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [locale, setLocale] = useState('en-US');
    const [currency, setCurrency] = useState('USD');
    const [divisor, setDivisor] = useState(null);
    const [minTip, setMinTip] = useState(null);
    const [maxTip, setMaxTip] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [userId, setUserId] = useState(null);
    const [displayName, setDisplayName] = useState(null);
    const [creatorIcon, setCreatorIcon] = useState(null);
    const [pageCustomizations, setPageCustomizations] = useState({
        backgroundColor: '#68C7CC',
        textColor: '#FFFFFF',
        showProfile: true,
        text: null
    });
    const [method, setMethod] = useState(null);
    const navigate = useNavigate();

    const [showReportModal, setShowReportModal] = useState(false);
    const [reportCategory, setReportCategory] = useState('abuse');
    const [reportDetails, setReportDetails] = useState('');
    const [reportEvidence, setReportEvidence] = useState(''); // comma or newline separated URLs
    const [reportEmail, setReportEmail] = useState(''); // optional contact email
    const [reportSubmitting, setReportSubmitting] = useState(false);
    const [reportError, setReportError] = useState('');
    const [reportSuccess, setReportSuccess] = useState(false);

    const [limitExceeded, setLimitExceeded] = useState(false);

    // Determine the access method (page, button, or qrCode)
    useEffect(() => {
        const determineAccessMethod = () => {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('method')) {
                const methodParam = urlParams.get('method');
                if (['page', 'button', 'qrCode'].includes(methodParam)) {
                    setMethod(methodParam);
                    return;
                }
            }

            setMethod('page');
        };

        determineAccessMethod();
    }, []);

    // Utilities for report submission
    const parseEvidenceUrls = (value) => {
        return value
            .split(/\n|,/)
            .map(s => s.trim())
            .filter(s => s.length > 0);
    };

    const checkTipLimits = async () => {
        try {
            const res = await fetch(`${apiBaseUrl}/tip-limits`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ linkId })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to fetch limits');
            }
            const data = await res.json();
            setLimitExceeded(data.anyExceeded ? data.anyExceeded : false);
        } catch (e) {
            console.error('Error fetching tip limits:', e);
            setLimitExceeded(false);
        }
    };

    const openReportModal = () => {
        setShowReportModal(true);
        setReportSuccess(false);
        setReportError('');
    };

    const closeReportModal = () => {
        if (reportSubmitting) return;
        setShowReportModal(false);
        setReportCategory('abuse');
        setReportDetails('');
        setReportEvidence('');
        setReportEmail('');
        setReportSubmitting(false);
        setReportError('');
        setReportSuccess(false);
    };

    const submitReport = async (e) => {
        e?.preventDefault?.();
        setReportError('');
        setReportSuccess(false);

        if (!reportDetails.trim()) {
            setReportError('Please provide details about your report.');
            return;
        }

        console.log('window location href: ', window.location.href);

        try {
            setReportSubmitting(true);
            const evidenceUrls = parseEvidenceUrls(reportEvidence);
            const payload = {
                target: {
                    type: 'account',
                    accountId: userId,
                    linkId,
                    contextUrl: window.location.href
                },
                category: reportCategory,
                details: reportDetails,
                evidenceUrls,
                reporter: {
                    email: reportEmail || undefined
                },
            };

            const res = await fetch(`${apiBaseUrl}/reports`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to submit report');
            }

            setReportSuccess(true);
        } catch (err) {
            console.error('Report submission failed:', err);
            setReportError('Something went wrong submitting your report. Please try again later.');
        } finally {
            setReportSubmitting(false);
        }
    };

    // Get the referrer information with enhanced detection
    const getReferrerInfo = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const referrer = document.referrer;
        console.log(`Referrer: ${referrer}`);
        let source = '';
        let fullReferrerUrl = '';

        // First priority: actual referrer URL if available
        if (referrer) {
            fullReferrerUrl = referrer;

            try {
                const referrerDomain = new URL(referrer).hostname;

                // Categorize common social media domains
                if (referrerDomain.includes('facebook.com') || referrerDomain.includes('fb.com')) {
                    source = 'facebook';
                } else if (referrerDomain.includes('instagram.com')) {
                    source = 'instagram';
                } else if (referrerDomain.includes('twitter.com') || referrerDomain.includes('x.com')) {
                    source = 'twitter';
                } else if (referrerDomain.includes('linkedin.com')) {
                    source = 'linkedin';
                } else {
                    // Use the domain as the source category
                    source = referrerDomain;
                }
            } catch (e) {
                // If URL parsing fails, keep source as 'referral'
            }
        }

        // Second priority: URL parameters that might indicate source when referrer is not available
        if (!fullReferrerUrl) {
            if (urlParams.has('fbclid')) {
                source = 'facebook';
            } else if (urlParams.has('gclid')) {
                source = 'google';
            } else if (urlParams.has('twclid')) {
                source = 'twitter';
            } else if (urlParams.has('utm_source')) {
                source = urlParams.get('utm_source');
            } else if (urlParams.has('ref') || urlParams.has('source')) {
                source = urlParams.get('ref') || urlParams.get('source');
            }
        }

        return {
            sourceUrl: fullReferrerUrl,
            sourceType: source
        };
    };

    const { sourceUrl, sourceType } = getReferrerInfo();
    console.log('Source URL:', sourceUrl);
    console.log('Source Type:', sourceType);

    console.log(linkId);

    // Function to get the visit ID from cookies
    const getVisitId = () => {
        const cookies = document.cookie.split('; ');
        const visitCookie = cookies.find(cookie => cookie.startsWith('tipl_visit_id='));
        return visitCookie ? visitCookie.split('=')[1] : null;
    };

    // Get visitId from cookie
    const visitId = getVisitId();
    console.log('Visit ID from cookie:', visitId);

    const checkLinkId = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${apiBaseUrl}/check-link-id`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ linkId }),
            });

            if (!response.ok) {
                throw new Error('Invalid link');
            }

            const data = await response.json();

            console.log('response from check-link-id:', data);

            const { minimumTip, suggestedTip, maximum, divisor } = data;
            const suggestedTipMainUnit = suggestedTip / divisor;
            const minimumTipMainUnit = minimumTip / divisor;
            const maximumTipMainUnit = maximum / divisor;

            setMoneyType(data.moneyType);
            setStripeOnboardingStatus(data.stripeOnboardingStatus);
            setStripeAccountId(data.stripeAccountId);
            setStripeProductId(data.stripeProductId);
            const viewerLocale = (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en-US';
            console.log('viewer locale: ', viewerLocale);
            setLocale(viewerLocale);
            setCurrency(data.currency);
            setDivisor(divisor);
            setMinTip(minimumTipMainUnit);
            setMaxTip(maximumTipMainUnit);
            setTipAmount(suggestedTipMainUnit);
            setUserId(data._id);
            setDisplayName(data.displayName);
            setCreatorIcon(data.icon);

            // Set page customizations if available
            if (data.pageCustomizations) {
                setPageCustomizations(data.pageCustomizations);
            }
        } catch (error) {
            console.error('Error checking link ID:', error);
            navigate("/404");
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('success') === 'true') {
            setIsSuccess(true);
            setIsLoading(false);
            return;
        }
        checkLinkId();
        checkTipLimits();
    }, []);

    const handlePayWithCard = () => {
        setShowCheckoutForm(true);
    };

    return (
        <div className="min-h-screen flex flex-col font-worksans" style={{ backgroundColor: pageCustomizations.backgroundColor }}>
            <div className="pt-4 pl-10">
                <button
                    className="text-white"
                    onClick={() => window.open('/', '_blank')}
                >
                    <img src={logo} className="h-12 w-auto" alt="tipl logo" />
                </button>
            </div>
            <main className="flex-1 relative">
                {/* Content container with scrolling */}
                <div className="p-4 flex justify-center items-center">
                    <div className={`w-full mx-auto ${showCheckoutForm ? 'max-w-5xl' : 'max-w-md'}`}>
                        {/* Creator Profile Section */}
                        {!isLoading && !isSuccess && (
                            <div className="flex flex-col items-center mb-6">
                                {pageCustomizations.showProfile && (
                                    <div className="w-20 h-20 rounded-full bg-white overflow-hidden flex items-center justify-center border-2 border-white mb-2">
                                        {creatorIcon ? (
                                            <img
                                                src={creatorIcon}
                                                alt="Creator's profile"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="text-[#68C7CC] text-2xl font-bold">
                                                {displayName ? displayName[0] : '?'}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <h2 className="text-xl font-semibold" style={{ color: pageCustomizations.textColor }}>
                                    {pageCustomizations.text ? ((pageCustomizations.text === 'Donate' || pageCustomizations.text === 'Contribute') ? (`${pageCustomizations.text} to ${displayName}`) : (`${pageCustomizations.text} ${displayName}`)) : (`Support ${displayName}`)}
                                </h2>
                            </div>
                        )}
                        {isLoading ? (
                            <div className="text-white text-center p-8 rounded-lg bg-[#5AB1B5]" role="status" aria-live="polite">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                                <p>Loading...</p>
                            </div>
                        ) : isSuccess ? (
                            <div className="text-white text-center p-8 rounded-lg bg-[#5AB1B5]" role="alert">
                                <h2 className="text-xl font-semibold mb-4">Thank you for your tip!</h2>
                                <button
                                    onClick={() => window.location.href = `/${linkId}`}
                                    className="bg-white text-[#68C7CC] px-6 py-2 rounded-lg font-semibold hover:bg-opacity-90"
                                >
                                    Send Another Tip
                                </button>
                            </div>
                        ) : stripeOnboardingStatus !== 'complete' ? (
                            <div className="text-white text-center p-8 rounded-lg bg-[#5AB1B5]" role="alert">
                                <h2 className="text-xl font-semibold mb-4">This Creator Hasn't Completed Their Setup</h2>
                                <p>Please check back later to support them!</p>
                            </div>
                        ) : !showCheckoutForm ? (
                            <div className="bg-white p-6 rounded-lg shadow-md">
                                {limitExceeded ? (
                                    <div className="mb-4 rounded text-black text-center" role="alert">
                                        <p className="mb-1 pt-2">This account has reached their tip limit for now. Please try again later.</p>
                                    </div>
                                ) : (
                                    <div>
                                        <TipInput
                                            tipAmount={tipAmount}
                                            setTipAmount={setTipAmount}
                                            minTip={minTip}
                                            maxTip={maxTip}
                                            currency={currency}
                                            locale={locale}
                                            name={name}
                                            setName={setName}
                                            message={message}
                                            setMessage={setMessage}
                                            displayName={displayName}
                                        />
                                        <button
                                            onClick={handlePayWithCard}
                                            disabled={!tipAmount || tipAmount < minTip || tipAmount > maxTip}
                                            // className={`w-20 mx-auto block font-bold py-2 px-4 rounded ${!tipAmount || tipAmount < minTip ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-700 text-white'}`}
                                            className={`w-a mx-auto block font-bold py-2 px-4 rounded ${!tipAmount || tipAmount < minTip || tipAmount > maxTip ? 'bg-gray-400 cursor-not-allowed' : 'bg-yellow-200 hover:bg-yellow-300 text-black'}`}

                                        >
                                            Tip {new Intl.NumberFormat(locale, {
                                                style: 'currency',
                                                currency: currency,
                                                minimumFractionDigits: Number.isInteger(Number(tipAmount)) ? 0 : 2,
                                                maximumFractionDigits: Number.isInteger(Number(tipAmount)) ? 0 : 2
                                            }).format(tipAmount)}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : stripeAccountId && showCheckoutForm && (
                            <CheckoutForm
                                stripeAccountId={stripeAccountId}
                                stripeProductId={stripeProductId}
                                tipAmount={tipAmount}
                                currency={currency}
                                divisor={divisor}
                                setShowCheckoutForm={setShowCheckoutForm}
                                sourceUrl={sourceUrl}
                                sourceType={sourceType}
                                userId={userId}
                                name={name}
                                message={message}
                                visitId={visitId}
                                method={method}
                            />
                        )}
                    </div>
                </div>
            </main>
            {/* Footer */}
            <footer className="mt-auto py-4 px-1 text-center text-white/80 text-sm">
                <div className="container mx-auto">
                    <span>&copy; {new Date().getFullYear()} Tipl</span>
                    <span className="mx-2">·</span>
                    <Link to="/privacy" className="underline hover:text-white">Privacy</Link>
                    <span className="mx-2">·</span>
                    <Link to="/terms" className="underline hover:text-white">Terms</Link>
                    <span className="mx-2">·</span>
                    <Link to="/contact" className="underline hover:text-white">Contact</Link>
                    <span className="mx-2">·</span>
                    <button
                        type="button"
                        onClick={openReportModal}
                        className="underline hover:text-white"
                        aria-haspopup="dialog"
                        aria-expanded={showReportModal}
                    >
                        Report
                    </button>
                </div>
            </footer>

            {showReportModal && (
                <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={closeReportModal} aria-hidden="true"></div>
                    <div className="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4 z-10 overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="report-modal-title">
                        <div className="max-h-[90vh] overflow-y-auto p-6">
                            <h2 id="report-modal-title" className="text-lg font-semibold mb-4">Report this page</h2>
                            <p className="text-sm text-gray-600 mb-4">If you believe that a page violates our <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">terms of service</a>, please report it to us. We keep all reports confidential.</p>

                            {reportError && (
                                <div className="mb-3 text-sm text-red-600" role="alert">{reportError}</div>
                            )}
                            {reportSuccess ? (
                                <div className="mb-4 p-3 rounded bg-green-50 text-green-800" role="status">Thank you. Your report has been submitted.</div>
                            ) : (
                                <form onSubmit={submitReport} className="text-sm">
                                    <label className="block text-sm font-medium mb-1" htmlFor="report-category">Reason</label>
                                    <select
                                        id="report-category"
                                        className="w-full border rounded px-3 py-2 mb-3"
                                        value={reportCategory}
                                        onChange={(e) => setReportCategory(e.target.value)}
                                        required
                                    >
                                        <option value="sexual">Nudity or sexual content</option>
                                        <option value="hate">Hate or hateful conduct</option>
                                        <option value="violence">Violence</option>
                                        <option value="impersonation">Impersonation</option>
                                        <option value="ip">Intellectual property infringement</option>
                                        <option value="illegal">Illegal content</option>
                                        <option value="self-harm">Self-harm or safety risk</option>
                                        <option value="scam">Scam or fake page</option>
                                        <option value="other">Other</option>
                                    </select>

                                    <label className="block text-sm font-medium mb-1" htmlFor="report-details">Details</label>
                                    <textarea
                                        id="report-details"
                                        className="w-full border rounded px-3 py-2 mb-3"
                                        rows={4}
                                        placeholder="Please explain what happened and why this content/page should be reviewed."
                                        value={reportDetails}
                                        onChange={(e) => setReportDetails(e.target.value)}
                                        required
                                    />

                                    <label className="block text-sm font-medium mb-1" htmlFor="report-evidence">Other relevant links (optional)</label>
                                    <textarea
                                        id="report-evidence"
                                        className="w-full border rounded px-3 py-2 mb-3"
                                        rows={2}
                                        placeholder="Paste links here, separated by commas or new lines"
                                        value={reportEvidence}
                                        onChange={(e) => setReportEvidence(e.target.value)}
                                    />

                                    <label className="block text-sm font-medium mb-1" htmlFor="report-email">Contact email (optional)</label>
                                    <input
                                        id="report-email"
                                        type="email"
                                        className="w-full border rounded px-3 py-2 mb-3"
                                        placeholder="you@example.com"
                                        value={reportEmail}
                                        onChange={(e) => setReportEmail(e.target.value)}
                                    />
                                    <div className="flex justify-end gap-3">
                                        <button type="button" onClick={closeReportModal} className="px-4 py-2 rounded border" disabled={reportSubmitting}>Cancel</button>
                                        <button type="submit" className={`px-4 py-2 rounded text-white ${reportSubmitting ? 'bg-gray-400' : 'bg-[#68C7CC] hover:bg-[#5AB1B5]'}`} disabled={reportSubmitting}>
                                            {reportSubmitting ? 'Submitting…' : 'Submit report'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}